import { createTheme, IDropdownStyles, ILinkProps, IMessageBarProps, INavStyles, ITextFieldStyles, ITextProps, Theme } from "@fluentui/react";

import { DARK_THEME_NAME, DEFAULT_THEME_NAME } from "../constants/constants";

// Accessible blue link color
export const linkColor = "#0065b5";

/**
 * Shared Dropdown styles that override the default low-contrast chevron with
 * pure black so the dropdown icon meets accessibility contrast requirements.
 */
export const DropdownStyles: Partial<IDropdownStyles> = {
  caretDown: {
    color: "#000000",
  },
};

/**
 * Darken the nav link text on hover so the color contrast of the selected item
 * (default ~4.05:1) meets the WCAG AA minimum of 4.5:1 for normal text.
 */
export const NavStyles: Partial<INavStyles> = {
  link: {
    selectors: {
      "&:hover .ms-Nav-linkText": {
        color: "#0057B7",
      },
    },
  },
};

/**
 * Styles for a read-only, borderless TextField used to display a file name via
 * its placeholder. Using `multiline` lets the placeholder wrap on narrow
 * screens, but Fluent's multiline default forces a 60px minHeight; overriding
 * it back to auto keeps the field a single line when no wrapping is needed.
 */
export const WrappingReadOnlyTextFieldStyles: Partial<ITextFieldStyles> = {
  fieldGroup: {
    minHeight: "auto",
  },
  field: {
    minHeight: "auto",
  },
};

const TextHighContrastStyles: ITextProps["styles"] = {
  root: {
    MsHighContrastAdjust: "auto",
  },
};

const MessageBarHighContrastStyles: IMessageBarProps["styles"] = {
  dismissal: {
    "& > span > i": {
      MsHighContrastAdjust: "auto !important",
    },
    "& > li": {
      MsHighContrastAdjust: "auto",
    },
  },
};

export const defaultTheme: Theme = {
  name: DEFAULT_THEME_NAME,
  ...createTheme({
    components: {
      Text: {
        styles: TextHighContrastStyles,
      },
      MessageBar: {
        styles: MessageBarHighContrastStyles,
      },
    },
    palette: {
      themePrimary: "#0078d4",
      themeLighterAlt: "#eff6fc",
      themeLighter: "#deecf9",
      themeLight: "#c7e0f4",
      themeTertiary: "#71afe5",
      themeSecondary: "#2b88d8",
      themeDarkAlt: "#106ebe",
      themeDark: "#005a9e",
      themeDarker: "#004578",
      neutralLighterAlt: "#faf9f8",
      neutralLighter: "#f3f2f1",
      neutralLight: "#edebe9",
      neutralQuaternaryAlt: "#e1dfdd",
      neutralQuaternary: "#d0d0d0",
      neutralTertiaryAlt: "#c8c6c4",
      neutralTertiary: "#a19f9d",
      neutralSecondary: "#605e5c",
      neutralPrimaryAlt: "#3b3a39",
      neutralPrimary: "#323130",
      neutralDark: "#201f1e",
      black: "#000000",
      white: "#ffffff",
      // Accessible colors
      blue: "#0078D4",
    },
  }),
};

const LinkDarkModeStyles: ILinkProps["styles"] = {
  root: {
    color: "#008cfa !important",
  },
};

export const darkTheme: Theme = {
  name: DARK_THEME_NAME,
  ...createTheme({
    components: {
      Link: {
        styles: LinkDarkModeStyles,
      },
      Text: {
        styles: TextHighContrastStyles,
      },
      MessageBar: {
        styles: MessageBarHighContrastStyles,
      },
    },
    semanticColors: {
      errorText: "#ee343c", // Color suggested by Accessibility Insights
    },
    palette: {
      themePrimary: "#58a6ff",
      themeLighterAlt: "#04070a",
      themeLighter: "#0e1b29",
      themeLight: "#1b324d",
      themeTertiary: "#366499",
      themeSecondary: "#4f93e0",
      themeDarkAlt: "#6aafff",
      themeDark: "#81bcff",
      themeDarker: "#a2cdff",
      neutralLighterAlt: "#131921",
      neutralLighter: "#19202a",
      neutralLight: "#242c39",
      neutralQuaternaryAlt: "#2b3442",
      neutralQuaternary: "#303a49",
      neutralTertiaryAlt: "#4b5668",
      neutralTertiary: "#dbe0e5",
      neutralSecondary: "#e0e5ea",
      neutralPrimaryAlt: "#e6eaee",
      neutralPrimary: "#c9d1d9",
      neutralDark: "#f2f4f6",
      black: "#f8f9fa",
      white: "#0d1117",
      // Accessible colors
      green: "#109510",
      red: "#ff1125",
      blue: "#007ede",
    },
  }),
};
